"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

export type ButtonBlockVariant = "solid" | "outline" | "text" | "ghost" | "link";
export type ButtonBlockColor = "primary" | "secondary" | "destructive";
export type ButtonBlockSize = "default" | "sm" | "lg" | "icon";

export interface ButtonBlockProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonBlockVariant;
  color?: ButtonBlockColor;
  size?: ButtonBlockSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  asChild?: boolean;
}

function mapClasses(
  variant: ButtonBlockVariant,
  color: ButtonBlockColor
): string {
  // Map high-level variant/color to centralized driver variants
  if (variant === "solid") {
    if (color === "secondary") return styleDrivers.button.variants.secondary;
    if (color === "destructive") return styleDrivers.button.variants.destructive;
    return styleDrivers.button.variants.default;
  }
  if (variant === "outline") {
    return styleDrivers.button.variants.outline;
  }
  if (variant === "ghost") {
    return styleDrivers.button.variants.ghost;
  }
  if (variant === "link") {
    return styleDrivers.button.variants.link;
  }
  // text (fallback to ghost)
  return styleDrivers.button.variants.ghost;
}

function mapSize(size: ButtonBlockSize): string {
  if (size === "sm") return styleDrivers.button.sizes.sm;
  if (size === "lg") return styleDrivers.button.sizes.lg;
  if (size === "icon") return styleDrivers.button.sizes.icon;
  return styleDrivers.button.sizes.default;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonBlockProps>(
  (
    {
      className,
      children,
      variant = "solid",
      color = "primary",
      size = "default",
      leftIcon,
      rightIcon,
      asChild,
      ...props
    },
    ref
  ) => {
    const classes = cn(
      styleDrivers.button.root,
      mapClasses(variant, color),
      mapSize(size),
      className
    );

    const content = (
      <button ref={ref} className={classes} {...props}>
        {leftIcon}
        {children}
        {rightIcon}
      </button>
    );

    // Support polymorphism via asChild (simple passthrough)
    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<{ className?: string }>;
      return React.cloneElement(child, {
        className: cn(child.props?.className, classes),
        ...(props as any),
      });
    }

    return content;
  }
);

Button.displayName = "Button";

export default Button;


